import React from "react";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, GraduationCap, MessageCircle, BookOpen, Award, Heart, AlertCircle, Layers } from "lucide-react";
import { LEARN_REIKI, SITE } from "@/lib/site";

const waUrl = (message) =>
  `https://wa.me/${SITE.phoneClean}?text=${encodeURIComponent(message)}`;

const ProgramCard = ({ p, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.9 }}
      data-testid={`program-${p.id}`}
      className="bg-[#F4F1EB] rounded-[2rem] overflow-hidden border border-[#EAE5D9] p-8 lg:p-14"
    >
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-6 pb-10 border-b border-[#EAE5D9]">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.28em] text-[#B35B47]">
            Learning Program
          </div>
          <h3 className="font-serif text-3xl lg:text-5xl mt-3 text-[#2C302E] leading-[1.05]">
            {p.name}
          </h3>
          <div className="mt-3 font-serif italic text-lg text-[#5C605A] leading-relaxed">
            {p.tagline}
          </div>
        </div>
        <div className="shrink-0 rounded-2xl bg-[#FAF9F6] border border-[#EAE5D9] px-6 py-4 text-right">
          <div className="text-xs uppercase tracking-[0.22em] text-[#8A8D86]">Energy Exchange</div>
          <div className="font-serif text-3xl text-[#B35B47] mt-1">{p.price}</div>
        </div>
      </div>

      {/* About */}
      <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.22em] text-[#8A8D86]">About the program</div>
          {p.about?.map((para, i) => (
            <p
              key={i}
              className={`mt-4 leading-relaxed ${i === 0 ? "text-[#2C302E] text-[1.05rem]" : "text-[#5C605A]"}`}
            >
              {para}
            </p>
          ))}
        </div>
        <div className="lg:col-span-5 rounded-2xl bg-[#FAF9F6] border border-[#EAE5D9] p-6 lg:p-7 self-start">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B35B47]">
            <Sparkles className="h-3.5 w-3.5 text-[#C5A880]" />
            {p.youWillExploreHeading || "You will explore"}
          </div>
          <ul className="mt-5 space-y-3">
            {p.youWillExplore.map((it, i) => (
              <li key={i} className="flex gap-3 text-[#2C302E]">
                <CheckCircle2 className="h-5 w-5 text-[#C5A880] shrink-0 mt-0.5" strokeWidth={1.6} />
                <span className="leading-relaxed">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Levels / Pillars */}
      <div className="mt-14">
        <div className="text-xs uppercase tracking-[0.22em] text-[#8A8D86] mb-6">
          {p.levelsHeading || "What you will learn"}
        </div>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
          {p.levels.map((lv, idx) => (
            <div
              key={idx}
              data-testid={`${p.id}-level-${idx + 1}`}
              className="rounded-2xl bg-[#FAF9F6] border border-[#EAE5D9] p-7 lg:p-8"
            >
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-[#C5A880]/20 text-[#B35B47] flex items-center justify-center font-serif text-lg">
                  {idx + 1}
                </span>
                <BookOpen className="h-4 w-4 text-[#C5A880]" strokeWidth={1.6} />
              </div>
              <h4 className="font-serif text-2xl lg:text-[1.6rem] mt-5 text-[#2C302E] leading-tight">
                {lv.title}
              </h4>
              <p className="mt-2 font-serif italic text-[#5C605A]">{lv.subtitle}</p>
              <ul className="mt-6 space-y-3">
                {lv.points.map((pt, i) => (
                  <li key={i} className="flex gap-3 text-sm text-[#2C302E] leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#B35B47] shrink-0" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {p.footnote && (
          <p className="mt-6 text-sm italic text-[#8A8D86] leading-relaxed">
            {p.footnote}
          </p>
        )}
      </div>

      {/* Who is this for */}
      {p.forWhom && (
        <div className="mt-14 rounded-2xl bg-[#FAF9F6] border border-[#EAE5D9] p-7 lg:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B35B47]">
            <Heart className="h-3.5 w-3.5 text-[#C5A880]" strokeWidth={1.6} />
            {p.forWhom.heading || "Who is this training for"}
          </div>
          <p className="mt-5 text-[#2C302E] leading-relaxed">{p.forWhom.intro}</p>
          <ul className="mt-5 grid md:grid-cols-2 gap-x-10 gap-y-3">
            {p.forWhom.bullets.map((b, i) => (
              <li key={i} className="flex gap-3 text-[#2C302E] leading-relaxed">
                <CheckCircle2 className="h-5 w-5 text-[#C5A880] shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          {p.forWhom.prerequisite && (
            <div className="mt-7 rounded-xl bg-[#B35B47]/10 border border-[#B35B47]/25 p-5 flex gap-3">
              <AlertCircle className="h-5 w-5 text-[#B35B47] shrink-0 mt-0.5" strokeWidth={1.6} />
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#B35B47]">{p.forWhom.prerequisiteLabel || "Prerequisite"}</div>
                <p className="mt-1 text-[#2C302E] leading-relaxed text-sm">
                  {p.forWhom.prerequisite}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modules / Curriculum */}
      {p.modules && p.modules.length > 0 && (
        <div className="mt-14">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B35B47]">
            <Layers className="h-3.5 w-3.5 text-[#C5A880]" strokeWidth={1.6} />
            {p.modulesHeading || "Program curriculum"}
          </div>
          {p.modulesNote && (
            <p className="mt-4 text-[#5C605A] leading-relaxed max-w-3xl">
              {p.modulesNote}
            </p>
          )}
          <ol className="mt-7 space-y-3">
            {p.modules.map((m, i) => (
              <li
                key={i}
                data-testid={`${p.id}-module-${i + 1}`}
                className="rounded-2xl bg-[#FAF9F6] border border-[#EAE5D9] p-6 lg:p-7 flex gap-5"
              >
                <span className="shrink-0 h-10 w-10 rounded-full bg-[#2C302E] text-[#FAF9F6] font-serif text-lg flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <h4 className="font-serif text-xl lg:text-2xl text-[#2C302E] leading-tight">
                    {m.title}
                  </h4>
                  <p className="mt-2 text-[#5C605A] leading-relaxed">{m.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Includes */}
      {p.includes && p.includes.length > 0 && (
        <div className="mt-14 rounded-2xl bg-[#2C302E] text-[#FAF9F6] p-8 lg:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#C5A880]">
            <Award className="h-3.5 w-3.5" />
            What is included
          </div>
          {p.includesNote && (
            <p className="mt-4 text-[#FAF9F6]/70 text-sm leading-relaxed">{p.includesNote}</p>
          )}
          <ul className="mt-6 grid md:grid-cols-2 gap-x-10 gap-y-4">
            {p.includes.map((it, i) => (
              <li key={i} className="flex gap-3 leading-relaxed">
                <CheckCircle2 className="h-5 w-5 text-[#C5A880] shrink-0 mt-0.5" strokeWidth={1.6} />
                <span className="text-[#FAF9F6]/90">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTA */}
      <div className="mt-10 flex flex-wrap items-center gap-5 justify-between">
        <div className="text-[#5C605A] max-w-md text-sm">
          Ready to begin? Message me on WhatsApp and I'll share the next
          cohort dates and the welcome package.
        </div>
        <a
          href={waUrl(p.bookingMessage)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`enroll-${p.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-[#2C302E] text-[#FAF9F6] px-7 py-3.5 text-sm tracking-wide hover:bg-[#B35B47] transition-colors"
        >
          <MessageCircle className="h-4 w-4" />
          {p.bookingLabel}
        </a>
      </div>
    </motion.div>
  );
};

const LearnReiki = () => {
  const programs = LEARN_REIKI.programs || [];

  return (
    <section id="learn" data-testid="learn-reiki-section" className="py-24 lg:py-32 bg-[#FAF9F6] relative overflow-hidden">
      <div className="pointer-events-none absolute top-20 right-0 h-[28rem] w-[28rem] rounded-full bg-[#9A8B8E]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-[26rem] w-[26rem] rounded-full bg-[#C5A880]/15 blur-3xl" />

      <div className="mx-auto max-w-7xl px-6 lg:px-12 relative z-10">
        {/* Section intro */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#B35B47]">
              <GraduationCap className="h-3.5 w-3.5 text-[#C5A880]" />
              — Learn Reiki
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#2C302E] leading-[1.05]">
              Become a <span className="italic">Reiki</span><br />Practitioner.
            </h2>
            <p className="mt-7 text-[#5C605A] leading-relaxed text-lg">
              Begin your Reiki learning journey with Aura Bliss Reiki. Explore
              Reiki step by step — from foundational energy-work practices to
              advanced spiritual learning and teacher-level development. Choose
              the level that aligns with your experience and aspirations.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-[#9A8B8E]/30 to-[#C5A880]/20 rounded-[2rem] blur-2xl" />
            <div className="relative aspect-square overflow-hidden rounded-[1.75rem]">
              <img
                src={LEARN_REIKI.image}
                alt="Learn Reiki"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </motion.div>
        </div>

        {/* Program cards */}
        <div className="space-y-10 lg:space-y-14">
          {programs.map((p, i) => (
            <ProgramCard key={p.id} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LearnReiki;
